import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Avatar, Button, Popover, Stack } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import { DialogComponent } from '../../../components/basics/DialogComponent';
import { useAuthContext } from '../../../context/AuthContextProvider';
import { usePostsContext } from '../../../context/PostsProvider';
import { Post } from '../../../models/Post';
import { getAvatar } from '../../../models/User';
import { CustomCard } from '../../../themes/theme/GlobalStyledComponents';
import { checkResponseStatus } from '../../../utils/api/response';
import { formatDateWithBars } from '../../../utils/utils';
import { CreateEditPostModal } from '../CreateEditPostModal/CreateEditPostModal';

const ImageContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  width: '100%',
  overflow: 'hidden'
});

const StyledCardMedia = styled(CardMedia)({
  width: '100%',
  height: 'auto',
  objectFit: 'cover',
  '@media (max-width: 600px)': {
    maxHeight: '300px'
  }
});

const ButtonContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0
});

const ButtonLeft = styled(IconButton)({
  position: 'absolute',
  left: 0
});

const ButtonRight = styled(IconButton)({
  position: 'absolute',
  right: 0
});

interface Props {
  post: Post;
  onClick: () => void;
}

export const PostCard = ({ post, onClick }: Props) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [showEditPost, setShowEditPost] = React.useState<Post | undefined>(undefined);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);

  const { user } = useAuthContext();
  const { deletePost } = usePostsContext();

  const POST_OWNER = user?.id === post?.criado_por.id && user?.adm_intranet == 'S';

  const handleNextImage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % post.arquivos.length);
  };

  const handlePrevImage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + post?.arquivos?.length) % post?.arquivos?.length);
  };

  const handleEditPost = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    handleClosePopover();
    event.stopPropagation();
    setShowEditPost(post);
  };

  const handleClose = () => {
    setShowEditPost(undefined);
  };

  const handleDeletePost = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    handleClosePopover();
    event.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleDeletePostClose = () => {
    setShowDeleteDialog(false);
  };

  const handleDeletePostConfirm = async () => {
    if (post.id) {
      const response = await deletePost(post.id);

      if (checkResponseStatus(response)) {
        setShowDeleteDialog(false);
        //mostrar o snackbar
      }
    }
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const getPostImage = (type: string, base64: string) => {
    if (!base64) return '';

    let data = `data:${type};base64,${base64}`;
    return data;
  };

  return (
    <>
      <CustomCard onClick={onClick}>
        <CardHeader
          avatar={<Avatar aria-label="user avatar">{getAvatar(post.criado_por.nome)}</Avatar>}
          title={post.criado_por.nome}
          subheader={formatDateWithBars(post.criado_em)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            POST_OWNER && (
              <IconButton onClick={(event) => handleOpenPopover(event)}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )
          }
        />
        <CardContent>
          <p style={{ whiteSpace: 'pre-line' }}>{post?.descricao}</p>
        </CardContent>
        {post?.arquivos?.length > 0 && (
          <ImageContainer>
            {post?.arquivos?.[currentImageIndex]?.tipo === 'image/jpeg' || post.arquivos?.[currentImageIndex]?.tipo === 'image/png' ? (
              <StyledCardMedia
                component="img"
                image={getPostImage(post?.arquivos?.[currentImageIndex]?.tipo || '', post?.arquivos?.[currentImageIndex]?.base64 || '')}
                alt="post image"
              />
            ) : (
              <iframe
                title="post file"
                src={getPostImage(post?.arquivos?.[currentImageIndex]?.tipo || '', post?.arquivos?.[currentImageIndex]?.base64 || '')}
                style={{
                  width: '100%',
                  height: '500px', // Ajuste a altura aqui conforme necessário
                  border: 'none'
                }}
              />
            )}
            {post?.arquivos?.length > 1 && (
              <ButtonContainer>
                <ButtonLeft
                  aria-label="previous image"
                  onClick={handlePrevImage}
                  disabled={currentImageIndex === 0}
                  sx={{
                    backgroundColor: 'white',
                    color: 'black',
                    borderRadius: '50%',
                    '&:hover': {
                      backgroundColor: 'black',
                      color: 'white'
                    }
                  }}
                >
                  <LeftOutlined />
                </ButtonLeft>
                <ButtonRight
                  aria-label="next image"
                  onClick={handleNextImage}
                  disabled={currentImageIndex === post?.arquivos?.length - 1}
                  sx={{
                    backgroundColor: 'white',
                    color: 'black',
                    opacity: 0.8,
                    borderRadius: '50%',
                    '&:hover': {
                      backgroundColor: 'black',
                      color: 'white'
                    }
                  }}
                >
                  <RightOutlined />
                </ButtonRight>
              </ButtonContainer>
            )}
          </ImageContainer>
        )}
      </CustomCard>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <Stack sx={{ display: 'flex' }}>
          <Button startIcon={<EditIcon />} onClick={handleEditPost}>
            Editar
          </Button>
          <Button startIcon={<DeleteIcon />} onClick={handleDeletePost}>
            Excluir
          </Button>
        </Stack>
      </Popover>
      {showEditPost && <CreateEditPostModal editionData={showEditPost} open={!!showEditPost} onClose={handleClose} />}
      {showDeleteDialog && (
        <DialogComponent
          title="Excluir Publicação"
          description="Tem certeza que deseja excluir essa publicação?"
          open={showDeleteDialog}
          onCancel={handleDeletePostClose}
          onConfirm={handleDeletePostConfirm}
        />
      )}
    </>
  );
};
